import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle2, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { Button } from './ui/Button';
interface PrePaymentFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: FormData) => void;
  ticketName: string;
  ticketPrice: string;
  ticketType?: 'festival-only' | 'accommodation' | 'double-alone';
}
interface FormData {
  fullName: string;
  phone: string;
  email: string;
  gender: 'male' | 'female' | '';
  nightChoice: 'thursday' | 'friday' | '';
  roomType: 'double' | 'triple' | '';
  sharedRoomAcknowledgment: boolean;
  roommate1Name: string;
  roommate2Name: string;
  companionName: string;
  companionPhone: string;
  companionGender: 'male' | 'female' | '';
  competitiveTrack: string[];
  rowingType: 'sup' | 'race' | 'surfski' | 'surfer' | 'atmosphere' | '';
  belongsToClub: 'yes' | 'no' | '';
  clubName: string;
  rentBoard: 'yes' | 'no' | '';
  healthDeclaration: boolean;
  medicalCheckups: boolean;
  riskAwareness: boolean;
  liabilityWaiver: boolean;
  medicalTreatment: boolean;
  photographyConsent: boolean;
  cancellationPolicy: boolean;
  whatsappGroup: boolean;
}
export function WaterTribeFestPrePaymentForm({
  isOpen,
  onClose,
  onSubmit,
  ticketName,
  ticketPrice,
  ticketType = 'accommodation'
}: PrePaymentFormProps) {
  const [showCancellationPolicy, setShowCancellationPolicy] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    phone: '',
    email: '',
    gender: '',
    nightChoice: '',
    roomType: '',
    sharedRoomAcknowledgment: false,
    roommate1Name: '',
    roommate2Name: '',
    companionName: '',
    companionPhone: '',
    companionGender: '',
    competitiveTrack: [],
    rowingType: '',
    belongsToClub: '',
    clubName: '',
    rentBoard: '',
    healthDeclaration: false,
    medicalCheckups: false,
    riskAwareness: false,
    liabilityWaiver: false,
    medicalTreatment: false,
    photographyConsent: false,
    cancellationPolicy: false,
    whatsappGroup: false
  });
  const isFormValid = () => {
    const basicFieldsValid = formData.fullName.trim() !== '' && formData.phone.trim() !== '' && formData.email.trim() !== '' && formData.gender !== '';
    // Companion name required for tickets with companion
    const companionFieldsValid = ticketName.includes('+ כרטיס מלווה') ? formData.companionName.trim() !== '' && formData.companionPhone.trim() !== '' && formData.companionGender !== '' : true;
    // Night choice required only for "one night" ticket
    const nightChoiceValid = ticketName.includes('לילה אחד') ? formData.nightChoice !== '' : true;
    // Room fields only required for accommodation tickets (not for festival-only or double-alone)
    // Skip roommate validation for tickets with companion
    const roomFieldsValid = ticketType === 'accommodation' && !ticketName.includes('+ כרטיס מלווה') ? formData.roomType !== '' && formData.sharedRoomAcknowledgment && formData.roommate1Name.trim() !== '' : ticketType === 'accommodation' && ticketName.includes('+ כרטיס מלווה') ? formData.roomType !== '' : true;
    const declarationsValid = formData.healthDeclaration && formData.medicalCheckups && formData.riskAwareness && formData.liabilityWaiver && formData.medicalTreatment && formData.photographyConsent && formData.cancellationPolicy;
    return basicFieldsValid && companionFieldsValid && nightChoiceValid && roomFieldsValid && declarationsValid;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isFormValid()) {
      onSubmit(formData);
    }
  };
  const handleCheckboxChange = (field: keyof FormData) => {
    setFormData(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };
  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  return <AnimatePresence data-id="element-484" data-mp="1kbzjuqer9ila">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[80]" onClick={onClose} data-id="element-485" data-mp="2t61txs5meh9b" />

          <div className="fixed inset-0 z-[90] overflow-y-auto" dir="ltr" data-id="element-486" data-mp="328b0k6xrpqhf">
            <div className="min-h-full flex items-center justify-center p-4 md:p-8" data-id="element-487" data-mp="2x85h3e1njaaf">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} className="w-full max-w-4xl bg-white rounded-3xl shadow-2xl overflow-hidden" dir="rtl" onClick={e => e.stopPropagation()} data-id="element-488" data-mp="3c5fnwa3cnrzt">
                <div className="flex-1 overflow-y-auto" data-id="element-489" data-mp="3uvdzv1a9q58q">
                  {/* Header */}
                  <div className="sticky top-0 bg-gradient-to-br from-indigo-600 to-purple-600 text-white p-6 md:p-8 z-10 shadow-lg" data-id="element-490" data-mp="3pxm3nv2lbwyq">
                    <button onClick={onClose} className="absolute top-4 left-4 md:top-6 md:left-6 bg-white/20 hover:bg-white/30 rounded-full p-2 transition-colors" data-id="element-491" data-mp="de19rgxzjj0w">
                      <X className="w-5 h-5 md:w-6 md:h-6" data-id="element-492" data-mp="3g3nv03llybg" />
                    </button>
                    <h2 className="text-2xl md:text-3xl font-bold mb-3" data-id="element-493" data-mp="8dvr2q9p16uy">
                      טופס הרשמה לפסטיבל
                    </h2>
                    <div className="flex items-center justify-between text-sm md:text-base bg-white/20 backdrop-blur-sm rounded-xl p-4" data-id="element-494" data-mp="3qqtl6gjt15h7">
                      <span className="font-medium" data-id="element-495" data-mp="3gu8ezrw1tobs">{ticketName}</span>
                      <span className="font-bold text-lg" data-id="element-496" data-mp="3gw1odey1qmnh">₪{ticketPrice}</span>
                    </div>
                  </div>

                  {/* Form */}
                  <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-8" data-id="element-497" data-mp="37ta7v0qbd0oz">
                    {/* Personal Information */}
                    <div className="space-y-4" data-id="element-498" data-mp="3bum2xlryie4k">
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-3 pb-3 border-b-2 border-indigo-100" data-id="element-499" data-mp="1ic4z9nccinu7">
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-100 rounded-full flex items-center justify-center shrink-0" data-id="element-500" data-mp="1dkwpgq73t0gi">
                          <span className="text-indigo-600 font-bold text-sm md:text-base" data-id="element-501" data-mp="18k8ww0388bk6">
                            1
                          </span>
                        </div>
                        פרטים אישיים
                      </h3>

                      <div className="space-y-4" data-id="element-502" data-mp="1wd7lq179605n">
                        <div data-id="element-503" data-mp="1wad2ue3w0nec">
                          <label className="block text-sm md:text-base font-medium text-gray-700 mb-2" data-id="element-504" data-mp="1rcjubef43haw">
                            שם מלא <span className="text-red-500" data-id="element-505" data-mp="1nag75sj2yo9s">*</span>
                          </label>
                          <input type="text" required value={formData.fullName} onChange={e => handleInputChange('fullName', e.target.value)} className="w-full px-4 py-3 md:py-4 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all" placeholder="הזן שם מלא" data-id="element-506" data-mp="fyzav3vuhf9u" />
                        </div>

                        <div data-id="element-507" data-mp="2rix75872bm9m">
                          <label className="block text-sm md:text-base font-medium text-gray-700 mb-2" data-id="element-508" data-mp="2wk4c6zhtm7cl">
                            טלפון <span className="text-red-500" data-id="element-509" data-mp="31c4hz8kknuh3">*</span>
                          </label>
                          <input type="tel" required value={formData.phone} onChange={e => handleInputChange('phone', e.target.value)} className="w-full px-4 py-3 md:py-4 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all" placeholder="050-1234567" data-id="element-510" data-mp="3694dwqnpjguh" />
                        </div>

                        <div data-id="element-511" data-mp="3k7kt75zmyfo8">
                          <label className="block text-sm md:text-base font-medium text-gray-700 mb-2" data-id="element-512" data-mp="1kxcmgms4q73k">
                            אימייל <span className="text-red-500" data-id="element-513" data-mp="1pv4j1u3pyhe3">*</span>
                          </label>
                          <input type="email" required value={formData.email} onChange={e => handleInputChange('email', e.target.value)} className="w-full px-4 py-3 md:py-4 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all" placeholder="example@email.com" data-id="element-514" data-mp="3rsemzw0qsgxf" />
                        </div>
                      </div>
                    </div>

                    {/* Companion Information (if ticket includes companion) */}
                    {ticketName.includes('+ כרטיס מלווה') && <div className="space-y-4" data-id="element-515" data-mp="34utk35esj96a">
                        <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-3 pb-3 border-b-2 border-green-100" data-id="element-516" data-mp="3itb4at5hm3jg">
                          <div className="w-8 h-8 md:w-10 md:h-10 bg-green-100 rounded-full flex items-center justify-center shrink-0" data-id="element-517" data-mp="3ds3y2yiztcew">
                            <span className="text-green-600 font-bold text-sm md:text-base" data-id="element-518" data-mp="t3g4tjwxlvi6">
                              2
                            </span>
                          </div>
                          פרטי מלווה
                        </h3>

                        <div className="space-y-4 bg-green-50 p-6 rounded-xl border-2 border-green-200" data-id="element-519" data-mp="2g1qpocjid4cb">
                          <div data-id="element-520" data-mp="2228wzm33d6xo">
                            <label className="block text-sm md:text-base font-medium text-gray-700 mb-2" data-id="element-521" data-mp="26zhinwng7t3o">
                              שם מלא של המלווה{' '}
                              <span className="text-red-500" data-id="element-522" data-mp="2upljjw995eqz">*</span>
                            </label>
                            <input type="text" required value={formData.companionName} onChange={e => handleInputChange('companionName', e.target.value)} className="w-full px-4 py-3 md:py-4 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all" placeholder="הזן שם מלא של המלווה" data-id="element-523" data-mp="2ypvpj8x1o55u" />
                          </div>

                          <div data-id="element-524" data-mp="3ifpkfvv230ea">
                            <label className="block text-sm md:text-base font-medium text-gray-700 mb-2" data-id="element-525" data-mp="3igqym97dc308">
                              טלפון המלווה{' '}
                              <span className="text-red-500" data-id="element-526" data-mp="l2l47gblv2x5">*</span>
                            </label>
                            <input type="tel" required value={formData.companionPhone} onChange={e => handleInputChange('companionPhone', e.target.value)} className="w-full px-4 py-3 md:py-4 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all" placeholder="050-1234567" data-id="element-527" data-mp="71ijp809e0oc" />
                          </div>

                          <div data-id="element-528" data-mp="3jk8o9t2wg0gq">
                            <label className="block text-sm md:text-base font-medium text-gray-700 mb-3" data-id="element-529" data-mp="3jl0aweeiqnuv">
                              מין המלווה <span className="text-red-500" data-id="element-530" data-mp="3ojlbwg1lhbkd">*</span>
                            </label>
                            <div className="flex gap-4" data-id="element-531" data-mp="3ak2ccbmun8jo">
                              <label className="flex items-center gap-2 cursor-pointer" data-id="element-532" data-mp="3fin1ng0kc7tc">
                                <input type="radio" name="companionGender" value="male" checked={formData.companionGender === 'male'} onChange={e => setFormData({
                            ...formData,
                            companionGender: 'male'
                          })} className="w-4 h-4 text-green-600 focus:ring-green-500" data-id="element-533" data-mp="2vw1yxh5fu78g" />
                                <span className="text-sm md:text-base" data-id="element-534" data-mp="30u3lic22oooo">
                                  זכר
                                </span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer" data-id="element-535" data-mp="39vjv4hl2cmru">
                                <input type="radio" name="companionGender" value="female" checked={formData.companionGender === 'female'} onChange={e => setFormData({
                            ...formData,
                            companionGender: 'female'
                          })} className="w-4 h-4 text-green-600 focus:ring-green-500" data-id="element-536" data-mp="3ok7mebnvmabz" />
                                <span className="text-sm md:text-base" data-id="element-537" data-mp="3skzw160nuh8s">
                                  נקבה
                                </span>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>}

                    {/* Event Details - Accommodation Section (conditional) */}
                    {ticketType === 'accommodation' && <div className="space-y-4" data-id="element-538" data-mp="390fo8jwmcmmu">
                        <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-3 pb-3 border-b-2 border-indigo-100" data-id="element-539" data-mp="38zo4q7gaqfcx">
                          <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-100 rounded-full flex items-center justify-center shrink-0" data-id="element-540" data-mp="3iyddnaq7sph1">
                            <span className="text-indigo-600 font-bold text-sm md:text-base" data-id="element-541" data-mp="3mye2zqeuql2v">
                              2
                            </span>
                          </div>
                          פרטי לינה
                        </h3>

                        <div className="space-y-4" data-id="element-542" data-mp="1l1vf1qyy0aop">
                          {/* Room Type */}
                          <div data-id="element-543" data-mp="1gborzvk2wy8k">
                            <label className="block text-sm md:text-base font-medium text-gray-700 mb-3" data-id="element-544" data-mp="1gadntumq1ogx">
                              סוג החדר <span className="text-red-500" data-id="element-545" data-mp="1bcvcjxl8ex5r">*</span>
                            </label>
                            <div className="space-y-2" data-id="element-546" data-mp="17cbi51s1m7nb">
                              <label className="flex items-center gap-2 cursor-pointer p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-547" data-mp="241j245d0k8p4">
                                <input type="radio" name="roomType" value="double" checked={formData.roomType === 'double'} onChange={e => setFormData({
                            ...formData,
                            roomType: 'double'
                          })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-548" data-mp="1z2ps4mwxqztp" />
                                <span className="text-sm md:text-base" data-id="element-549" data-mp="emcmuon99ea7">
                                  זוגי
                                </span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-550" data-mp="22gy2m043qurb">
                                <input type="radio" name="roomType" value="triple" checked={formData.roomType === 'triple'} onChange={e => setFormData({
                            ...formData,
                            roomType: 'triple'
                          })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-551" data-mp="1tjdwacrdu89u" />
                                <span className="text-sm md:text-base" data-id="element-552" data-mp="2q693dhlgcp5g">
                                  חדר פרמיום גדול- טריפל (עד 3 מבוגרים בחדר)
                                </span>
                              </label>
                            </div>
                          </div>

                          {/* Shared Room Acknowledgment - Hide when companion ticket is selected */}
                          {!ticketName.includes('+ כרטיס מלווה') && <label className="flex items-start gap-3 md:gap-4 p-4 md:p-5 bg-amber-50 rounded-xl cursor-pointer hover:bg-amber-100 transition-colors border-2 border-amber-200" data-id="element-553" data-mp="xb7xh62igqim">
                              <input type="checkbox" checked={formData.sharedRoomAcknowledgment} onChange={() => handleCheckboxChange('sharedRoomAcknowledgment')} className="mt-1 w-5 h-5 md:w-6 md:h-6 text-indigo-600 rounded focus:ring-indigo-500 shrink-0" data-id="element-554" data-mp="179f9rbluukea" />
                              <span className="text-sm md:text-base text-gray-700 leading-relaxed" data-id="element-555" data-mp="el9o8mmz8rde">
                                <strong data-id="element-556" data-mp="9nq8tjejm8jv">
                                  ידוע לי כי הזמנה זו היא עבור לינה בחדר משותף
                                </strong>{' '}
                                - אני מאשר/ת כי מצאתי שותף/ה לחדר מראש,
                                ובאחריותי לוודא שגם השותף/ה מבצע/ת הרשמה והסדרה
                                של התשלום באופן עצמאי{' '}
                                <span className="text-red-500" data-id="element-557" data-mp="3n00dutjzk0ej">*</span>
                              </span>
                            </label>}

                          {/* Roommate 1 - Hide when companion ticket is selected */}
                          {!ticketName.includes('+ כרטיס מלווה') && <div data-id="element-558" data-mp="20myftf7wt03d">
                              <label className="block text-sm md:text-base font-medium text-gray-700 mb-2" data-id="element-559" data-mp="20orpz4hqe2g4">
                                שם שותף/ה 1 לחדר{' '}
                                <span className="text-red-500" data-id="element-560" data-mp="17zcbu5urrtfz">*</span>
                              </label>
                              <input type="text" required={ticketType === 'accommodation' && !ticketName.includes('+ כרטיס מלווה')} value={formData.roommate1Name} onChange={e => handleInputChange('roommate1Name', e.target.value)} className="w-full px-4 py-3 md:py-4 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all" placeholder="שם מלא של שותף/ה" data-id="element-561" data-mp="1cx43qzvezfk6" />
                            </div>}

                          {/* Roommate 2 (conditional) - Hide when companion ticket is selected */}
                          {formData.roomType === 'triple' && !ticketName.includes('+ כרטיס מלווה') && <div data-id="element-562" data-mp="32j4i0hxslpga">
                                <label className="block text-sm md:text-base font-medium text-gray-700 mb-2" data-id="element-563" data-mp="2xrutjknzcs5z">
                                  רק בחדר טריפל - שם שותף/ה 2 לחדר (אופציונלי)
                                </label>
                                <input type="text" value={formData.roommate2Name} onChange={e => handleInputChange('roommate2Name', e.target.value)} className="w-full px-4 py-3 md:py-4 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all" placeholder="שם מלא של שותף/ה שני (לא חובה)" data-id="element-564" data-mp="2org6afrgh2zu" />
                              </div>}
                        </div>
                      </div>}

                    {/* Event Details - Activity Section */}
                    <div className="space-y-4" data-id="element-565" data-mp="8zs9qlgctchk">
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-3 pb-3 border-b-2 border-indigo-100" data-id="element-566" data-mp="8xxo14x9dgcf">
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-100 rounded-full flex items-center justify-center shrink-0" data-id="element-567" data-mp="wawup5ov9h9s">
                          <span className="text-indigo-600 font-bold text-sm md:text-base" data-id="element-568" data-mp="iih0mudy0tl0">
                            {ticketName.includes('+ כרטיס מלווה') ? ticketType === 'accommodation' ? '4' : ticketType === 'double-alone' ? '3' : '3' : ticketType === 'accommodation' ? '3' : ticketType === 'double-alone' ? '2' : '2'}
                          </span>
                        </div>
                        פרטי השתתפות
                      </h3>

                      <div className="space-y-4" data-id="element-569" data-mp="4ixs40h7zg86">
                        {/* Night Choice - Only for "one night" ticket */}
                        {ticketName.includes('לילה אחד') && <div data-id="element-570" data-mp="1tyx6bvzp3vuo">
                            <label className="block text-sm md:text-base font-medium text-gray-700 mb-3" data-id="element-571" data-mp="1ywyrqnklg79b">
                              בחרו לילה לינה:{' '}
                              <span className="text-red-500" data-id="element-572" data-mp="291f61cifdgb0">*</span>
                            </label>
                            <div className="flex gap-4" data-id="element-573" data-mp="243x4w8ang5dk">
                              <label className="flex items-center gap-2 cursor-pointer p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors flex-1" data-id="element-574" data-mp="2htqtqqm5mpna">
                                <input type="radio" name="nightChoice" value="thursday" checked={formData.nightChoice === 'thursday'} onChange={e => setFormData({
                            ...formData,
                            nightChoice: 'thursday'
                          })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-575" data-mp="2d0xp02qorhb7" />
                                <span className="text-sm md:text-base font-medium" data-id="element-576" data-mp="2wrt17bwb3v9t">
                                  חמישי
                                </span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors flex-1" data-id="element-577" data-mp="12ds3wvgengbu">
                                <input type="radio" name="nightChoice" value="friday" checked={formData.nightChoice === 'friday'} onChange={e => setFormData({
                            ...formData,
                            nightChoice: 'friday'
                          })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-578" data-mp="4rowt51cu1w2" />
                                <span className="text-sm md:text-base font-medium" data-id="element-579" data-mp="3myd0cqgragzm">
                                  שישי
                                </span>
                              </label>
                            </div>
                          </div>}

                        {/* Gender */}
                        <div data-id="element-580" data-mp="3rvbq6wte01s0">
                          <label className="block text-sm md:text-base font-medium text-gray-700 mb-3" data-id="element-581" data-mp="3rv3hzi87lkn5">
                            מין <span className="text-red-500" data-id="element-582" data-mp="1b9u36pufuc1r">*</span>
                          </label>
                          <div className="flex gap-4" data-id="element-583" data-mp="1788p51663rpz">
                            <label className="flex items-center gap-2 cursor-pointer" data-id="element-584" data-mp="1eon8mncw5de8">
                              <input type="radio" name="gender" value="male" checked={formData.gender === 'male'} onChange={e => setFormData({
                            ...formData,
                            gender: 'male'
                          })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-585" data-mp="1jp1jse9sh3gh" />
                              <span className="text-sm md:text-base" data-id="element-586" data-mp="1xhhgkxc3k58y">זכר</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer" data-id="element-587" data-mp="22epx6thlk183">
                              <input type="radio" name="gender" value="female" checked={formData.gender === 'female'} onChange={e => setFormData({
                            ...formData,
                            gender: 'female'
                          })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-588" data-mp="1nplhpk26sb3o" />
                              <span className="text-sm md:text-base" data-id="element-589" data-mp="2l7twovhhx8am">נקבה</span>
                            </label>
                          </div>
                        </div>

                        {/* Competitive Track - Hide for companion-only tickets */}
                        {ticketName !== 'כרטיס מלווה בלבד' && <div data-id="element-590" data-mp="2m35p4iv10g2v">
                            <label className="block text-sm md:text-base font-medium text-gray-700 mb-3" data-id="element-591" data-mp="2h286y6a0nm1d">
                              מעוניין להשתתף במסלול הישגי? (ניתן לבחור יותר
                              ממסלול אחד)
                            </label>
                            <div className="space-y-2" data-id="element-592" data-mp="27au3jney222y">
                              {[{
                          value: 'no',
                          label: 'לא'
                        }, {
                          value: 'sprint',
                          label: 'מקצה ספרינט'
                        }, {
                          value: 'technical',
                          label: 'מקצה טכני'
                        }, {
                          value: 'longDistance',
                          label: 'מקצה לונג דיסטנס'
                        }].map(option => <label key={option.value} className="flex items-center gap-2 cursor-pointer p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-593" data-mp="8ttlc9n2k2a9">
                                  <input type="checkbox" name="competitiveTrack" value={option.value} checked={formData.competitiveTrack.includes(option.value)} onChange={e => {
                            const value = option.value;
                            setFormData(prev => ({
                              ...prev,
                              competitiveTrack: e.target.checked ? [...prev.competitiveTrack, value] : prev.competitiveTrack.filter(v => v !== value)
                            }));
                          }} className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500" data-id="element-594" data-mp="drlaivwbxaar" />
                                  <span className="text-sm md:text-base" data-id="element-595" data-mp="32l8rlshnmn0q">
                                    {option.label}
                                  </span>
                                </label>)}
                            </div>
                          </div>}

                        {/* Rowing Type - Hide for companion-only tickets */}
                        {ticketName !== 'כרטיס מלווה בלבד' && <div data-id="element-596" data-mp="2t8jc6rqvdc06">
                            <label className="block text-sm md:text-base font-medium text-gray-700 mb-3" data-id="element-597" data-mp="2of6t0x4ylxet">
                              חותר ב-
                            </label>
                            <div className="space-y-2" data-id="element-598" data-mp="2x81ynryc9v54">
                              {[{
                          value: 'sup',
                          label: 'סאפ'
                        }, {
                          value: 'race',
                          label: 'רייס'
                        }, {
                          value: 'surfski',
                          label: 'סרפסקי'
                        }, {
                          value: 'surfer',
                          label: 'גולש'
                        }, {
                          value: 'atmosphere',
                          label: 'סופג אווירה'
                        }].map(option => <label key={option.value} className="flex items-center gap-2 cursor-pointer p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-599" data-mp="3tdlk2idn2367">
                                  <input type="radio" name="rowingType" value={option.value} checked={formData.rowingType === option.value} onChange={e => setFormData({
                            ...formData,
                            rowingType: option.value as any
                          })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-600" data-mp="3joadb2e85alh" />
                                  <span className="text-sm md:text-base" data-id="element-601" data-mp="2vszat0bh3xsr">
                                    {option.label}
                                  </span>
                                </label>)}
                            </div>
                          </div>}

                        {/* Rowing Club - Hide for companion-only tickets */}
                        {ticketName !== 'כרטיס מלווה בלבד' && <div data-id="element-602" data-mp="xj0fx5jn5lam">
                            <label className="block text-sm md:text-base font-medium text-gray-700 mb-3" data-id="element-603" data-mp="sicfxudfpu49">
                              שייך למועדון חתירה?
                            </label>
                            <div className="flex gap-4 mb-3" data-id="element-604" data-mp="nl2sgk9lvsj2">
                              <label className="flex items-center gap-2 cursor-pointer" data-id="element-605" data-mp="ilgdo6e4q485">
                                <input type="radio" name="belongsToClub" value="yes" checked={formData.belongsToClub === 'yes'} onChange={e => setFormData({
                            ...formData,
                            belongsToClub: 'yes'
                          })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-606" data-mp="1g6vqkgjyiizi" />
                                <span className="text-sm md:text-base" data-id="element-607" data-mp="11gy6abp5edfl">כן</span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer" data-id="element-608" data-mp="1u7on16ttjo59">
                                <input type="radio" name="belongsToClub" value="no" checked={formData.belongsToClub === 'no'} onChange={e => setFormData({
                            ...formData,
                            belongsToClub: 'no',
                            clubName: ''
                          })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-609" data-mp="1u5vbpe89gfqx" />
                                <span className="text-sm md:text-base" data-id="element-610" data-mp="2gheuqajnxwmz">לא</span>
                              </label>
                            </div>

                            {formData.belongsToClub === 'yes' && <div data-id="element-611" data-mp="2pf8opetyge6z">
                                <label className="block text-sm md:text-base font-medium text-gray-700 mb-2" data-id="element-612" data-mp="2u7ib4kvv2w4i">
                                  במידה וכן, לאיזה מועדון?
                                </label>
                                <input type="text" value={formData.clubName} onChange={e => handleInputChange('clubName', e.target.value)} className="w-full px-4 py-3 md:py-4 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all" placeholder="שם המועדון" data-id="element-613" data-mp="345y3er6mkbfl" />
                              </div>}
                          </div>}

                        {/* Rent Board - Hide for companion-only tickets */}
                        {ticketName !== 'כרטיס מלווה בלבד' && <div data-id="element-614" data-mp="2c6pvpakp933">
                            <label className="block text-sm md:text-base font-medium text-gray-700 mb-3" data-id="element-615" data-mp="2czvuo1pjuk6">
                              מעוניין להשכיר גלשן לאירוע?
                            </label>
                            <div className="flex gap-4" data-id="element-616" data-mp="36r6kthexphuf">
                              <label className="flex items-center gap-2 cursor-pointer" data-id="element-617" data-mp="3khjrzhk04sip">
                                <input type="radio" name="rentBoard" value="yes" checked={formData.rentBoard === 'yes'} onChange={e => setFormData({
                            ...formData,
                            rentBoard: 'yes'
                          })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-618" data-mp="3kje8mjo8pygz" />
                                <span className="text-sm md:text-base" data-id="element-619" data-mp="12jlig0uhiopv">כן</span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer" data-id="element-620" data-mp="17bvgk45r6v38">
                                <input type="radio" name="rentBoard" value="no" checked={formData.rentBoard === 'no'} onChange={e => setFormData({
                            ...formData,
                            rentBoard: 'no'
                          })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-621" data-mp="tk89rae6q467" />
                                <span className="text-sm md:text-base" data-id="element-622" data-mp="1q8em6jhcm0v1">לא</span>
                              </label>
                            </div>
                          </div>}
                      </div>
                    </div>

                    {/* Health & Safety Declarations */}
                    <div className="space-y-4" data-id="element-623" data-mp="tqakkgl0my2">
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-3 pb-3 border-b-2 border-indigo-100" data-id="element-624" data-mp="x47veae25yn">
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-100 rounded-full flex items-center justify-center shrink-0" data-id="element-625" data-mp="e9on29myc828">
                          <span className="text-indigo-600 font-bold text-sm md:text-base" data-id="element-626" data-mp="j7ylqu7vxqsl">
                            {ticketName.includes('+ כרטיס מלווה') ? ticketType === 'accommodation' ? '5' : ticketType === 'double-alone' ? '4' : '4' : ticketType === 'accommodation' ? '4' : ticketType === 'double-alone' ? '3' : '3'}
                          </span>
                        </div>
                        הצהרות בריאות ובטיחות
                      </h3>

                      <div className="space-y-3" data-id="element-627" data-mp="5i4xoe47fajx">
                        <label className="flex items-start gap-3 md:gap-4 p-4 md:p-5 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors border-2 border-transparent hover:border-indigo-100" data-id="element-628" data-mp="5gjzmeiol9hb">
                          <input type="checkbox" checked={formData.healthDeclaration} onChange={() => handleCheckboxChange('healthDeclaration')} className="mt-1 w-5 h-5 md:w-6 md:h-6 text-indigo-600 rounded focus:ring-indigo-500 shrink-0" data-id="element-629" data-mp="3dy81g8s74f2z" />
                          <span className="text-sm md:text-base text-gray-700 leading-relaxed" data-id="element-630" data-mp="1r2joxi0c3ps4">
                            אני מצהיר/ה כי אני בריא/ה, בכושר גופני מתאים, וכי
                            אין לי כל מגבלה רפואית או גופנית המונעת ממני להשתתף
                            בפעילות הספורט ו/או במקצים השונים באירוע.
                          </span>
                        </label>

                        <label className="flex items-start gap-3 md:gap-4 p-4 md:p-5 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors border-2 border-transparent hover:border-indigo-100" data-id="element-631" data-mp="17d96hw97eqqb">
                          <input type="checkbox" checked={formData.medicalCheckups} onChange={() => handleCheckboxChange('medicalCheckups')} className="mt-1 w-5 h-5 md:w-6 md:h-6 text-indigo-600 rounded focus:ring-indigo-500 shrink-0" data-id="element-632" data-mp="ye4b2ysxvdid" />
                          <span className="text-sm md:text-base text-gray-700 leading-relaxed" data-id="element-633" data-mp="ogq3zpswo12q">
                            ידוע לי כי על פי חוק הספורט, האחריות לביצוע בדיקות
                            רפואיות תקופתיות חלה עליי, ואני מאשר/ת כי עשיתי כן
                            וכי נמצאתי כשיר/ה לפעילות המתוכננת.
                          </span>
                        </label>

                        <label className="flex items-start gap-3 md:gap-4 p-4 md:p-5 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors border-2 border-transparent hover:border-indigo-100" data-id="element-634" data-mp="9s81qjr5vntm">
                          <input type="checkbox" checked={formData.riskAwareness} onChange={() => handleCheckboxChange('riskAwareness')} className="mt-1 w-5 h-5 md:w-6 md:h-6 text-indigo-600 rounded focus:ring-indigo-500 shrink-0" data-id="element-635" data-mp="12ipzd5948wzn" />
                          <span className="text-sm md:text-base text-gray-700 leading-relaxed" data-id="element-636" data-mp="1gfv3v0t6lnss">
                            אני מודע/ת לכך שהשתתפות בפעילות ספורטיבית כרוכה
                            במאמץ גופני ובסיכונים פציעה אינהרנטיים. אני בוחר/ת
                            להשתתף בפעילות מרצוני החופשי ולאחר ששקלתי את
                            הסיכונים.
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Liability Waiver */}
                    <div className="space-y-4" data-id="element-637" data-mp="3apa2rvc6f2qd">
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-3 pb-3 border-b-2 border-indigo-100" data-id="element-638" data-mp="3aql5drtvy4fw">
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-100 rounded-full flex items-center justify-center shrink-0" data-id="element-639" data-mp="2i0cwyrp5iyei">
                          <span className="text-indigo-600 font-bold text-sm md:text-base" data-id="element-640" data-mp="2wy5ajeb51cq2">
                            {ticketName.includes('+ כרטיס מלווה') ? ticketType === 'accommodation' ? '6' : ticketType === 'double-alone' ? '5' : '5' : ticketType === 'accommodation' ? '5' : ticketType === 'double-alone' ? '4' : '4'}
                          </span>
                        </div>
                        שחרור מאחריות
                      </h3>

                      <div className="space-y-3" data-id="element-641" data-mp="2hudk31kln2jg">
                        <label className="flex items-start gap-3 md:gap-4 p-4 md:p-5 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors border-2 border-transparent hover:border-indigo-100" data-id="element-642" data-mp="2hv5dq6umyq73">
                          <input type="checkbox" checked={formData.liabilityWaiver} onChange={() => handleCheckboxChange('liabilityWaiver')} className="mt-1 w-5 h-5 md:w-6 md:h-6 text-indigo-600 rounded focus:ring-indigo-500 shrink-0" data-id="element-643" data-mp="2qt7d6hyq1as9" />
                          <span className="text-sm md:text-base text-gray-700 leading-relaxed" data-id="element-644" data-mp="35k7uzdlcyh12">
                            אני מוותר/ת בזאת באופן מלא ומוחלט על כל טענה, דרישה
                            או תביעה כנגד מארגני האירוע, המדריכים, נותני החסות
                            או בעלי המקום, בגין כל נזק גוף, נזק רכוש או אובדן
                            שייגרם לי במהלך האירוע או כתוצאה ממנו, למעט במקרים
                            של רשלנות פושעת מצד המארגנים.
                          </span>
                        </label>

                        <label className="flex items-start gap-3 md:gap-4 p-4 md:p-5 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors border-2 border-transparent hover:border-indigo-100" data-id="element-645" data-mp="3oni53qop4cf9">
                          <input type="checkbox" checked={formData.medicalTreatment} onChange={() => handleCheckboxChange('medicalTreatment')} className="mt-1 w-5 h-5 md:w-6 md:h-6 text-indigo-600 rounded focus:ring-indigo-500 shrink-0" data-id="element-646" data-mp="3fqhj1qp3mqez" />
                          <span className="text-sm md:text-base text-gray-700 leading-relaxed" data-id="element-647" data-mp="6iyc361p7uky">
                            אני מאשר/ת למארגנים להעניק לי עזרה ראשונה או לפנות
                            אותי לטיפול רפואי במידת הצורך, ומסכים/ה לשאת בעלויות
                            הכרוכות בכך (כגון אמבולנס) ככל שיידרש.
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Photography & Media */}
                    <div className="space-y-4" data-id="element-648" data-mp="2tfpws8j15das">
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-3 pb-3 border-b-2 border-indigo-100" data-id="element-649" data-mp="2tfg58gic6y2z">
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-100 rounded-full flex items-center justify-center shrink-0" data-id="element-650" data-mp="2jq6dx2vbhjfx">
                          <span className="text-indigo-600 font-bold text-sm md:text-base" data-id="element-651" data-mp="25q595cbxlrv6">
                            {ticketName.includes('+ כרטיס מלווה') ? ticketType === 'accommodation' ? '7' : ticketType === 'double-alone' ? '6' : '6' : ticketType === 'accommodation' ? '6' : ticketType === 'double-alone' ? '5' : '5'}
                          </span>
                        </div>
                        צילום ופרסום
                      </h3>

                      <label className="flex items-start gap-3 md:gap-4 p-4 md:p-5 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors border-2 border-transparent hover:border-indigo-100" data-id="element-652" data-mp="3q4fe9xqqw3w8">
                        <input type="checkbox" checked={formData.photographyConsent} onChange={() => handleCheckboxChange('photographyConsent')} className="mt-1 w-5 h-5 md:w-6 md:h-6 text-indigo-600 rounded focus:ring-indigo-500 shrink-0" data-id="element-653" data-mp="1eiws4h45r24x" />
                        <span className="text-sm md:text-base text-gray-700 leading-relaxed" data-id="element-654" data-mp="1og1eoi1x0zm9">
                          אני נותן/ת בזאת את הסכמתי למארגני האירוע או למי מטעמם,
                          לצלם אותי במהלך הפעילות. אני מסכים/ה כי המארגנים יוכלו
                          לעשות שימוש בתמונות ובסרטונים אלו לצרכי תיעוד, פרסום,
                          שיווק ויחסי ציבור של האירוע, בכל מדיה, ללא הגבלת זמן
                          וללא תמורה כספית.
                        </span>
                      </label>
                    </div>

                    {/* Cancellation Policy */}
                    <div className="space-y-4" data-id="element-655" data-mp="cuceo9xt8vrk">
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-3 pb-3 border-b-2 border-indigo-100" data-id="element-656" data-mp="cvniuav645j7">
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-100 rounded-full flex items-center justify-center shrink-0" data-id="element-657" data-mp="3s42va2hfopgf">
                          <span className="text-indigo-600 font-bold text-sm md:text-base" data-id="element-658" data-mp="qoxb1mo3hos">
                            {ticketName.includes('+ כרטיס מלווה') ? ticketType === 'accommodation' ? '8' : ticketType === 'double-alone' ? '7' : '7' : ticketType === 'accommodation' ? '7' : ticketType === 'double-alone' ? '6' : '6'}
                          </span>
                        </div>
                        מדיניות ביטולים
                      </h3>

                      <div className="border-2 border-gray-200 rounded-xl overflow-hidden" data-id="element-659" data-mp="3j4pjv49r1qt2">
                        <button type="button" onClick={() => setShowCancellationPolicy(!showCancellationPolicy)} className="w-full flex items-center justify-between p-4 md:p-5 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-660" data-mp="tf4arjq5hry0">
                          <span className="font-medium text-gray-900 text-sm md:text-base" data-id="element-661" data-mp="2iuyaiivtg6ut">
                            לחץ לקריאת מדיניות הביטולים
                          </span>
                          {showCancellationPolicy ? <ChevronUp className="w-5 h-5 md:w-6 md:h-6 text-gray-600 shrink-0" data-id="element-662" data-mp="294vhwjslwux0" /> : <ChevronDown className="w-5 h-5 md:w-6 md:h-6 text-gray-600 shrink-0" data-id="element-663" data-mp="247lmmnijdp19" />}
                        </button>

                        {showCancellationPolicy && <div className="p-4 md:p-5 bg-white space-y-3 text-sm md:text-base text-gray-700 border-t-2 border-gray-200" data-id="element-664" data-mp="1z5w8gz00ov8y">
                            <p className="font-medium" data-id="element-665" data-mp="1q6sm9m5jzzq8">תנאי ביטול:</p>
                            <ul className="list-disc list-inside space-y-2 mr-4" data-id="element-666" data-mp="1q95dkpx7bmfy">
                              <li data-id="element-667" data-mp="1la2bnecipwc7">
                                ביטול בין שלושה שבועות לשבועיים לפני מועד האירוע
                                – יחויבו 50% מעלות ההשתתפות
                              </li>
                              <li data-id="element-668" data-mp="3fkfnlix4ov6j">
                                ביטול עד שבוע לפני מועד האירוע – יחויבו 70%
                                מעלות ההשתתפות
                              </li>
                              <li data-id="element-669" data-mp="1krj8i1y74d6">
                                ביטול במהלך השבוע שלפני מועד האירוע (7 ימים
                                ומטה) – יחויבו 100% מעלות ההשתתפות
                              </li>
                            </ul>
                          </div>}
                      </div>

                      <label className="flex items-start gap-3 md:gap-4 p-4 md:p-5 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors border-2 border-transparent hover:border-indigo-100" data-id="element-670" data-mp="pac4x8phkfq5">
                        <input type="checkbox" checked={formData.cancellationPolicy} onChange={() => handleCheckboxChange('cancellationPolicy')} className="mt-1 w-5 h-5 md:w-6 md:h-6 text-indigo-600 rounded focus:ring-indigo-500 shrink-0" data-id="element-671" data-mp="bgui9f1i6mvw" />
                        <span className="text-sm md:text-base text-gray-700 leading-relaxed font-medium" data-id="element-672" data-mp="144gwdh8cx9x0">
                          קראתי והבנתי את מדיניות הביטולים
                        </span>
                      </label>
                    </div>

                    {/* WhatsApp Group (Optional) */}
                    <div className="space-y-4" data-id="element-673" data-mp="1ldrmfh6vzoqy">
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-3 pb-3 border-b-2 border-green-100" data-id="element-674" data-mp="1lh6xm8xfwdn3">
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-green-100 rounded-full flex items-center justify-center shrink-0" data-id="element-675" data-mp="1ugah3e0iqv24">
                          <span className="text-green-600 font-bold text-sm md:text-base" data-id="element-676" data-mp="1zcr9wlinxxlj">
                            {ticketName.includes('+ כרטיס מלווה') ? ticketType === 'accommodation' ? '9' : ticketType === 'double-alone' ? '8' : '8' : ticketType === 'accommodation' ? '8' : ticketType === 'double-alone' ? '7' : '7'}
                          </span>
                        </div>
                        קבוצת וואטסאפ (אופציונלי)
                      </h3>

                      <label className="flex items-start gap-3 md:gap-4 p-4 md:p-5 bg-green-50 rounded-xl cursor-pointer hover:bg-green-100 transition-colors border-2 border-transparent hover:border-green-200" data-id="element-677" data-mp="i22a6jrztgr7">
                        <input type="checkbox" checked={formData.whatsappGroup} onChange={() => handleCheckboxChange('whatsappGroup')} className="mt-1 w-5 h-5 md:w-6 md:h-6 text-green-600 rounded focus:ring-green-500 shrink-0" data-id="element-678" data-mp="rtf5xmgwwr2i" />
                        <span className="text-sm md:text-base text-gray-700 leading-relaxed" data-id="element-679" data-mp="11sxnxxd9i2rx">
                          אני מעוניין/ת שתוסיפו אותי לקבוצת הוואטסאפ השקטה של
                          האירוע
                        </span>
                      </label>
                    </div>

                    {/* Submit Button */}
                    <div className="sticky bottom-0 bg-white pt-6 pb-2 border-t-2 border-gray-200 -mx-6 md:-mx-8 px-6 md:px-8" data-id="element-680" data-mp="3cimba15y025c">
                      {!isFormValid() && <div className="flex items-start gap-3 mb-4 p-4 bg-amber-50 border-2 border-amber-200 rounded-xl" data-id="element-681" data-mp="37lchk9bit4bp">
                          <AlertCircle className="w-5 h-5 md:w-6 md:h-6 text-amber-600 shrink-0 mt-0.5" data-id="element-682" data-mp="1n4392k34pyr7" />
                          <p className="text-sm md:text-base text-amber-800 leading-relaxed" data-id="element-683" data-mp="1s2oa2lq7gmgp">
                            יש למלא את כל השדות ולאשר את כל ההצהרות הנדרשות לפני
                            המעבר לתשלום
                          </p>
                        </div>}

                      <Button type="submit" disabled={!isFormValid()} className={`w-full py-4 md:py-5 text-base md:text-lg font-bold rounded-xl transition-all ${isFormValid() ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`} data-id="element-684" data-mp="149xzafu1g980">
                        {isFormValid() ? <span className="flex items-center justify-center gap-2" data-id="element-685" data-mp="2ppx2l0omivs7">
                            <CheckCircle2 className="w-5 h-5 md:w-6 md:h-6" data-id="element-686" data-mp="2tqgzq4975zec" />
                            המשך לתשלום
                          </span> : 'נא למלא את כל השדות'}
                      </Button>
                    </div>
                  </form>
                </div>
              </motion.div>
            </div>
          </div>
        </>}
    </AnimatePresence>;
}